import { useRef, useMemo, useCallback, useEffect } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import gsap from "gsap";
import type { OtherCursor } from "@/stores/cameraStore";

interface RealtimeCursorsProps {
  cursors: OtherCursor[];
  myCursor?: { gridX: number; gridZ: number; color?: string } | null;
}

const CURSOR_COLORS = [
  "#FF6B6B", // Red
  "#4ECDC4", // Teal
  "#FFE66D", // Yellow
  "#A8E6CF", // Mint
  "#FF8B94", // Pink
  "#B8B5FF", // Purple
  "#F7DC6F", // Gold
  "#85C1E9", // Blue
];

// 공유 지오메트리 (WebGL 컨텍스트 손실 방지)
const sharedSphereGeometry = new THREE.SphereGeometry(0.15, 16, 16);
const sharedRingGeometry = new THREE.RingGeometry(0.3, 0.5, 32);

// 머티리얼 캐시 (같은 색상의 커서들이 머티리얼 공유)
const materialCache = new Map<
  string,
  {
    sphere: THREE.ShaderMaterial;
    ring: THREE.ShaderMaterial;
    refCount: number;
  }
>();

// GUI 클리핑을 지원하는 커서 셰이더 (구체용)
const cursorSphereVertexShader = `
varying vec4 vGroundClipPos;
varying vec3 vNormal;
varying vec3 vViewPosition;

void main() {
  vNormal = normalize(normalMatrix * normal);
  
  // 바닥 지점(Y=0)의 클립 좌표 계산
  vec4 groundPos = vec4(position.x, 0.0, position.z, 1.0);
  vGroundClipPos = projectionMatrix * modelViewMatrix * groundPos;
  
  vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
  vViewPosition = -mvPosition.xyz;
  
  gl_Position = projectionMatrix * mvPosition;
}
`;

const cursorSphereFragmentShader = `
uniform vec3 uColor;
uniform vec3 uEmissive;
uniform float uEmissiveIntensity;
uniform float opacity;
// GUI 클리핑용 uniforms
uniform float uGUIControlPoint;
uniform vec2 uWorldXDir;
uniform vec2 uWorldZDir;
uniform float uEnableGUIClipping;
uniform float uTopLeftEnabled;
uniform float uTopRightEnabled;
uniform float uBottomLeftEnabled;
uniform float uBottomRightEnabled;
uniform float uTopLeftOffset;
uniform float uTopRightOffset;
uniform float uBottomLeftOffset;
uniform float uBottomRightOffset;

varying vec4 vGroundClipPos;
varying vec3 vNormal;
varying vec3 vViewPosition;

float sideOfLine(vec2 linePoint, vec2 lineDir, vec2 point) {
  vec2 perp = vec2(-lineDir.y, lineDir.x);
  return dot(point - linePoint, perp);
}

void main() {
  // GUI 클리핑
  if (uEnableGUIClipping > 0.5) {
    vec2 ndc = vGroundClipPos.xy / vGroundClipPos.w;
    float controlX = uGUIControlPoint * 2.0 - 1.0;
    
    // TopLeft
    vec2 topLeftCorner = vec2(-1.0, 1.0);
    vec2 baseTopLeftControl = vec2(controlX, 1.0);
    vec2 topLeftPerp = normalize(vec2(uWorldZDir.y, -uWorldZDir.x));
    float topLeftDistToCorner = length(baseTopLeftControl - topLeftCorner);
    vec2 topLeftControl = baseTopLeftControl + topLeftPerp * uTopLeftOffset * topLeftDistToCorner * 1.5;
    if (sideOfLine(topLeftControl, uWorldZDir, ndc) < 0.0 && uTopLeftEnabled > 0.5) discard;
    
    // TopRight
    vec2 topRightCorner = vec2(1.0, 1.0);
    vec2 baseTopRightControl = vec2(controlX, 1.0);
    vec2 topRightPerp = normalize(vec2(-uWorldXDir.y, uWorldXDir.x));
    float topRightDistToCorner = length(baseTopRightControl - topRightCorner);
    vec2 topRightControl = baseTopRightControl + topRightPerp * uTopRightOffset * topRightDistToCorner * 1.5;
    if (sideOfLine(topRightControl, uWorldXDir, ndc) > 0.0 && uTopRightEnabled > 0.5) discard;
    
    // BottomLeft
    vec2 bottomLeftCorner = vec2(-1.0, -1.0);
    vec2 baseBottomLeftControl = vec2(controlX, -1.0);
    vec2 bottomLeftPerp = normalize(vec2(uWorldXDir.y, -uWorldXDir.x));
    float bottomLeftDistToCorner = length(baseBottomLeftControl - bottomLeftCorner);
    vec2 bottomLeftControl = baseBottomLeftControl + bottomLeftPerp * uBottomLeftOffset * bottomLeftDistToCorner * 1.5;
    if (sideOfLine(bottomLeftControl, uWorldXDir, ndc) < 0.0 && uBottomLeftEnabled > 0.5) discard;
    
    // BottomRight
    vec2 bottomRightCorner = vec2(1.0, -1.0);
    vec2 baseBottomRightControl = vec2(controlX, -1.0);
    vec2 bottomRightPerp = normalize(vec2(-uWorldZDir.y, uWorldZDir.x));
    float bottomRightDistToCorner = length(baseBottomRightControl - bottomRightCorner);
    vec2 bottomRightControl = baseBottomRightControl + bottomRightPerp * uBottomRightOffset * bottomRightDistToCorner * 1.5;
    if (sideOfLine(bottomRightControl, uWorldZDir, ndc) > 0.0 && uBottomRightEnabled > 0.5) discard;
  }
  
  // 간단한 조명 계산
  vec3 normal = normalize(vNormal);
  vec3 viewDir = normalize(vViewPosition);
  float diffuse = max(dot(normal, vec3(0.0, 1.0, 0.5)), 0.3);
  
  vec3 finalColor = uColor * diffuse + uEmissive * uEmissiveIntensity;
  gl_FragColor = vec4(finalColor, opacity);
}
`;

// GUI 클리핑을 지원하는 커서 셰이더 (링용)
const cursorRingVertexShader = `
varying vec4 vGroundClipPos;

void main() {
  // 바닥 지점(Y=0)의 클립 좌표 계산
  vec4 groundPos = vec4(position.x, 0.0, position.z, 1.0);
  vGroundClipPos = projectionMatrix * modelViewMatrix * groundPos;
  
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

const cursorRingFragmentShader = `
uniform vec3 uColor;
uniform float opacity;
// GUI 클리핑용 uniforms
uniform float uGUIControlPoint;
uniform vec2 uWorldXDir;
uniform vec2 uWorldZDir;
uniform float uEnableGUIClipping;
uniform float uTopLeftEnabled;
uniform float uTopRightEnabled;
uniform float uBottomLeftEnabled;
uniform float uBottomRightEnabled;
uniform float uTopLeftOffset;
uniform float uTopRightOffset;
uniform float uBottomLeftOffset;
uniform float uBottomRightOffset;

varying vec4 vGroundClipPos;

float sideOfLine(vec2 linePoint, vec2 lineDir, vec2 point) {
  vec2 perp = vec2(-lineDir.y, lineDir.x);
  return dot(point - linePoint, perp);
}

void main() {
  // GUI 클리핑
  if (uEnableGUIClipping > 0.5) {
    vec2 ndc = vGroundClipPos.xy / vGroundClipPos.w;
    float controlX = uGUIControlPoint * 2.0 - 1.0;
    
    // TopLeft
    vec2 topLeftCorner = vec2(-1.0, 1.0);
    vec2 baseTopLeftControl = vec2(controlX, 1.0);
    vec2 topLeftPerp = normalize(vec2(uWorldZDir.y, -uWorldZDir.x));
    float topLeftDistToCorner = length(baseTopLeftControl - topLeftCorner);
    vec2 topLeftControl = baseTopLeftControl + topLeftPerp * uTopLeftOffset * topLeftDistToCorner * 1.5;
    if (sideOfLine(topLeftControl, uWorldZDir, ndc) < 0.0 && uTopLeftEnabled > 0.5) discard;
    
    // TopRight
    vec2 topRightCorner = vec2(1.0, 1.0);
    vec2 baseTopRightControl = vec2(controlX, 1.0);
    vec2 topRightPerp = normalize(vec2(-uWorldXDir.y, uWorldXDir.x));
    float topRightDistToCorner = length(baseTopRightControl - topRightCorner);
    vec2 topRightControl = baseTopRightControl + topRightPerp * uTopRightOffset * topRightDistToCorner * 1.5;
    if (sideOfLine(topRightControl, uWorldXDir, ndc) > 0.0 && uTopRightEnabled > 0.5) discard;
    
    // BottomLeft
    vec2 bottomLeftCorner = vec2(-1.0, -1.0);
    vec2 baseBottomLeftControl = vec2(controlX, -1.0);
    vec2 bottomLeftPerp = normalize(vec2(uWorldXDir.y, -uWorldXDir.x));
    float bottomLeftDistToCorner = length(baseBottomLeftControl - bottomLeftCorner);
    vec2 bottomLeftControl = baseBottomLeftControl + bottomLeftPerp * uBottomLeftOffset * bottomLeftDistToCorner * 1.5;
    if (sideOfLine(bottomLeftControl, uWorldXDir, ndc) < 0.0 && uBottomLeftEnabled > 0.5) discard;
    
    // BottomRight
    vec2 bottomRightCorner = vec2(1.0, -1.0);
    vec2 baseBottomRightControl = vec2(controlX, -1.0);
    vec2 bottomRightPerp = normalize(vec2(-uWorldZDir.y, uWorldZDir.x));
    float bottomRightDistToCorner = length(baseBottomRightControl - bottomRightCorner);
    vec2 bottomRightControl = baseBottomRightControl + bottomRightPerp * uBottomRightOffset * bottomRightDistToCorner * 1.5;
    if (sideOfLine(bottomRightControl, uWorldZDir, ndc) > 0.0 && uBottomRightEnabled > 0.5) discard;
  }
  
  gl_FragColor = vec4(uColor, opacity);
}
`;

function getMaterials(color: string, isMe: boolean) {
  const key = `${color}-${isMe}`;

  if (materialCache.has(key)) {
    const cached = materialCache.get(key)!;
    cached.refCount++;
    return cached;
  }

  const colorObj = new THREE.Color(color);

  // 구체용 ShaderMaterial (GUI 클리핑 지원)
  const sphereMaterial = new THREE.ShaderMaterial({
    uniforms: {
      uColor: { value: colorObj },
      uEmissive: { value: colorObj.clone() },
      uEmissiveIntensity: { value: isMe ? 2.0 : 1.5 },
      opacity: { value: isMe ? 0.9 : 0.8 },
      // GUI 클리핑용 uniforms (GUIClippingManager가 업데이트함)
      uGUIControlPoint: { value: 0.5 },
      uWorldXDir: { value: new THREE.Vector2(0.866, -0.5) },
      uWorldZDir: { value: new THREE.Vector2(-0.866, -0.5) },
      uEnableGUIClipping: { value: 0.0 },
      uTopLeftEnabled: { value: 1.0 },
      uTopRightEnabled: { value: 1.0 },
      uBottomLeftEnabled: { value: 1.0 },
      uBottomRightEnabled: { value: 1.0 },
      uTopLeftOffset: { value: 0.0 },
      uTopRightOffset: { value: 0.0 },
      uBottomLeftOffset: { value: 0.0 },
      uBottomRightOffset: { value: 0.0 },
    },
    vertexShader: cursorSphereVertexShader,
    fragmentShader: cursorSphereFragmentShader,
    transparent: true,
  });

  // 링용 ShaderMaterial (GUI 클리핑 지원)
  const ringMaterial = new THREE.ShaderMaterial({
    uniforms: {
      uColor: { value: colorObj.clone() },
      opacity: { value: isMe ? 0.5 : 0.3 },
      // GUI 클리핑용 uniforms (GUIClippingManager가 업데이트함)
      uGUIControlPoint: { value: 0.5 },
      uWorldXDir: { value: new THREE.Vector2(0.866, -0.5) },
      uWorldZDir: { value: new THREE.Vector2(-0.866, -0.5) },
      uEnableGUIClipping: { value: 0.0 },
      uTopLeftEnabled: { value: 1.0 },
      uTopRightEnabled: { value: 1.0 },
      uBottomLeftEnabled: { value: 1.0 },
      uBottomRightEnabled: { value: 1.0 },
      uTopLeftOffset: { value: 0.0 },
      uTopRightOffset: { value: 0.0 },
      uBottomLeftOffset: { value: 0.0 },
      uBottomRightOffset: { value: 0.0 },
    },
    vertexShader: cursorRingVertexShader,
    fragmentShader: cursorRingFragmentShader,
    transparent: true,
    side: THREE.DoubleSide,
  });

  const materials = {
    sphere: sphereMaterial,
    ring: ringMaterial,
    refCount: 1,
  };

  materialCache.set(key, materials);
  return materials;
}

function releaseMaterials(color: string, isMe: boolean) {
  const key = `${color}-${isMe}`;
  const cached = materialCache.get(key);

  if (cached) {
    cached.refCount--;
    if (cached.refCount <= 0) {
      cached.sphere.dispose();
      cached.ring.dispose();
      materialCache.delete(key);
    }
  }
}

/**
 * 실시간 커서 표시 컴포넌트
 * 내 커서 + 다른 사용자들의 커서를 표시
 *
 * 최적화:
 * - 단일 useFrame에서 모든 커서 애니메이션 처리
 * - 머티리얼 캐싱 및 공유
 */
export function RealtimeCursors({ cursors, myCursor }: RealtimeCursorsProps) {
  // 커서별 색상 할당
  const getCursorColor = useCallback((userId: string): string => {
    let hash = 0;
    for (let i = 0; i < userId.length; i++) {
      hash = (hash << 5) - hash + userId.charCodeAt(i);
      hash |= 0;
    }
    return CURSOR_COLORS[Math.abs(hash) % CURSOR_COLORS.length];
  }, []);

  // 모든 커서 refs를 저장 (애니메이션용)
  const cursorRefsMap = useRef<
    Map<
      string,
      { mesh: THREE.Mesh | null; ring: THREE.Mesh | null; isMe: boolean }
    >
  >(new Map());

  // 단일 useFrame에서 모든 커서 애니메이션 처리 (성능 최적화)
  useFrame(({ clock }) => {
    const time = clock.getElapsedTime();

    cursorRefsMap.current.forEach((refs) => {
      if (refs.mesh) {
        // 부드러운 부유 애니메이션
        const float = Math.sin(time * 2) * 0.1;
        refs.mesh.position.y = 0.5 + float;

        // 회전 애니메이션
        refs.mesh.rotation.y += refs.isMe ? 0.03 : 0.02;
      }

      if (refs.ring) {
        // 맥박 애니메이션
        const scale = 1 + Math.sin(time * (refs.isMe ? 4 : 3)) * 0.2;
        refs.ring.scale.set(scale, scale, 1);

        if (refs.isMe) {
          refs.ring.rotation.z += 0.01;
        }
      }
    });
  });

  return (
    <group name="realtime-cursors">
      {/* 내 커서 - 특별한 스타일 */}
      {myCursor && (
        <CursorIndicator
          key="my-cursor"
          id="my-cursor"
          gridX={myCursor.gridX}
          gridZ={myCursor.gridZ}
          color={myCursor.color || "#FFFFFF"}
          isMe={true}
          registerRefs={cursorRefsMap.current}
        />
      )}

      {/* 다른 사용자들의 커서 */}
      {cursors.map((cursor) => (
        <CursorIndicator
          key={cursor.userId}
          id={cursor.userId}
          gridX={cursor.gridX}
          gridZ={cursor.gridZ}
          color={getCursorColor(cursor.userId)}
          isMe={false}
          registerRefs={cursorRefsMap.current}
        />
      ))}
    </group>
  );
}

interface CursorIndicatorProps {
  id: string;
  gridX: number;
  gridZ: number;
  color: string;
  isMe: boolean;
  registerRefs: Map<
    string,
    { mesh: THREE.Mesh | null; ring: THREE.Mesh | null; isMe: boolean }
  >;
}

function CursorIndicator({
  id,
  gridX,
  gridZ,
  color,
  isMe,
  registerRefs,
}: CursorIndicatorProps) {
  const groupRef = useRef<THREE.Group>(null);
  const meshRef = useRef<THREE.Mesh>(null);
  const ringRef = useRef<THREE.Mesh>(null);

  // 캐시된 머티리얼 사용
  const materials = useMemo(() => getMaterials(color, isMe), [color, isMe]);

  // refs 등록/해제 (부모의 useFrame에서 사용)
  useEffect(() => {
    registerRefs.set(id, {
      mesh: meshRef.current,
      ring: ringRef.current,
      isMe,
    });

    return () => {
      registerRefs.delete(id);
      releaseMaterials(color, isMe);
    };
  }, [id, color, isMe, registerRefs]);

  // GSAP 부드러운 위치 보간
  useEffect(() => {
    if (groupRef.current) {
      gsap.to(groupRef.current.position, {
        x: gridX,
        z: gridZ,
        duration: 0.3,
        ease: "power2.out",
        overwrite: true,
      });
    }
  }, [gridX, gridZ]);

  return (
    <group ref={groupRef}>
      {/* 포인트 라이트 (조명) */}
      <pointLight
        color={color}
        intensity={isMe ? 2.5 : 2}
        distance={isMe ? 6 : 5}
        decay={2}
        position={[0, 0.5, 0]}
      />

      {/* 빛나는 구체 */}
      <mesh
        ref={meshRef}
        position={[0, 0.5, 0]}
        geometry={sharedSphereGeometry}
        material={materials.sphere}
      />

      {/* 바닥 링 */}
      <mesh
        ref={ringRef}
        scale={[isMe ? 4 : 3, isMe ? 4 : 3, 1]}
        position={[0, 0.05, 0]}
        rotation={[-Math.PI / 2, 0, 0]}
        geometry={sharedRingGeometry}
        material={materials.ring}
      />
    </group>
  );
}
